import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Clock,
  PackageSearch,
  TruckIcon,
  CheckCircle2,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { OrderAPI, OperationalAlertOrder } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';

interface NeedsAttentionProps {
  onOpenOrder: (orderId: string) => void;
}

type AlertsData = {
  late: { count: number; orders: OperationalAlertOrder[] };
  dueSoon: { count: number; orders: OperationalAlertOrder[] };
  awaitingAcceptance: { count: number; orders: OperationalAlertOrder[] };
  awaitingTransporter: { count: number; orders: OperationalAlertOrder[] };
};

const ageLabel = (o: OperationalAlertOrder): string => {
  const d = (h: number) => (h >= 24 ? `${Math.floor(h / 24)}d` : `${h}h`);
  if (o.overdueHours != null) return `${d(o.overdueHours)} overdue`;
  if (o.dueInHours != null) return `due in ${d(o.dueInHours)}`;
  if (o.waitingHours != null) return `waiting ${d(o.waitingHours)}`;
  return '';
};

const statusLabel = (s: string) =>
  s.split('_').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');

export default function NeedsAttention({ onOpenOrder }: NeedsAttentionProps) {
  const [data, setData] = useState<AlertsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await OrderAPI.getOperationalAlerts();
        if (!cancelled && res.success) setData(res.data.alerts);
      } catch {
        /* non-fatal */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const tiles = data
    ? [
        { key: 'late', label: 'Late', count: data.late.count, icon: AlertTriangle, tone: 'text-red-600', bg: 'bg-red-50', ring: 'hover:border-red-300' },
        { key: 'dueSoon', label: 'Due soon', count: data.dueSoon.count, icon: Clock, tone: 'text-amber-600', bg: 'bg-amber-50', ring: 'hover:border-amber-300' },
        { key: 'awaitingAcceptance', label: 'Awaiting supplier', count: data.awaitingAcceptance.count, icon: PackageSearch, tone: 'text-yellow-600', bg: 'bg-yellow-50', ring: 'hover:border-yellow-300' },
        { key: 'awaitingTransporter', label: 'No transporter', count: data.awaitingTransporter.count, icon: TruckIcon, tone: 'text-blue-600', bg: 'bg-blue-50', ring: 'hover:border-blue-300' },
      ]
    : [];

  const totalIssues = data
    ? data.late.count + data.dueSoon.count + data.awaitingAcceptance.count + data.awaitingTransporter.count
    : 0;

  // Most urgent orders to surface inline: late first, then no-transporter, then awaiting acceptance.
  const urgent: Array<OperationalAlertOrder & { reason: string; tone: string }> = [];
  if (data) {
    const push = (list: OperationalAlertOrder[], reason: string, tone: string) =>
      list.forEach((o) => {
        if (urgent.length < 6 && !urgent.some((u) => u._id === o._id)) urgent.push({ ...o, reason, tone });
      });
    push(data.late.orders, 'Late', 'text-red-600');
    push(data.awaitingTransporter.orders, 'No transporter', 'text-blue-600');
    push(data.awaitingAcceptance.orders, 'Awaiting supplier', 'text-yellow-600');
    push(data.dueSoon.orders, 'Due soon', 'text-amber-600');
  }

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Needs Attention
          </div>
          {!loading && totalIssues > 0 && (
            <span className="text-xs font-medium text-muted-foreground">{totalIssues} flagged</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 rounded-md border bg-muted/30 animate-pulse" />
            ))}
          </div>
        ) : !data || totalIssues === 0 ? (
          <div className="flex items-center gap-2 rounded-md border bg-green-50 px-4 py-3 text-sm text-green-700">
            <CheckCircle2 className="h-4 w-4" />
            All orders are on track — nothing needs attention right now.
          </div>
        ) : (
          <>
            {/* Summary tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {tiles.map((t) => {
                const Icon = t.icon;
                return (
                  <div
                    key={t.key}
                    className={`rounded-md border px-3 py-2.5 transition-colors ${t.ring}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`rounded-md ${t.bg} p-1.5`}>
                        <Icon className={`h-4 w-4 ${t.tone}`} />
                      </span>
                      <span className={`text-2xl font-bold leading-none ${t.count > 0 ? t.tone : 'text-muted-foreground'}`}>
                        {t.count}
                      </span>
                    </div>
                    <div className="mt-1.5 text-xs font-medium text-muted-foreground">{t.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Urgent order list */}
            {urgent.length > 0 && (
              <div className="mt-3 divide-y rounded-md border">
                {urgent.map((o) => (
                  <button
                    key={o._id}
                    onClick={() => onOpenOrder(o._id)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">#{o.orderNumber}</span>
                        <span className={`text-xs font-semibold ${o.tone}`}>{o.reason}</span>
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {o.customerName} · {statusLabel(o.status)} · {ageLabel(o)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-medium tabular-nums">{formatCurrency(o.totalAmount || 0)}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
